import { RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import onView from '@/hooks/onView';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/**
 * Reveals scoped to a section:
 *  [data-reveal]      text/blocks fade + rise (elements entering together are staggered)
 *  [data-reveal-img]  curtain clip reveal, inner <img> settles from scale
 *  [data-parallax]    gentle scrubbed vertical drift (value = yPercent)
 * Entrances are visibility-based (IntersectionObserver), so they work in editor frames too.
 */
export default function useSectionReveal(scope: RefObject<HTMLElement | null>) {
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(scope);
      const offs: Array<() => void> = [];

      // Text / blocks — queue elements that enter in the same frame and stagger them
      let queue: Element[] = [];
      let raf = 0;
      const flush = () => {
        raf = 0;
        const els = queue;
        queue = [];
        gsap.fromTo(els, { y: 36, opacity: 0 }, {
          y: 0, opacity: 1, duration: 1, ease: 'power3.out', stagger: 0.08, overwrite: true,
        });
      };
      q('[data-reveal]').forEach((el: HTMLElement) => {
        gsap.set(el, { opacity: 0, y: 36 });
        offs.push(onView(el, () => {
          queue.push(el);
          if (!raf) raf = requestAnimationFrame(flush);
        }, () => gsap.set(el, { opacity: 0, y: 36, overwrite: true }), '0px 0px -8% 0px'));
      });

      q('[data-reveal-img]').forEach((el: HTMLElement) => {
        const img = el.querySelector('img');
        const tl = gsap.timeline({ paused: true });
        tl.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.3, ease: 'power3.inOut' });
        if (img) tl.fromTo(img, { scale: 1.25 }, { scale: 1, duration: 1.8, ease: 'power3.out' }, 0);
        offs.push(onView(el, () => tl.restart(), () => tl.pause(0)));
      });

      q('[data-parallax]').forEach((el: HTMLElement) => {
        const amt = Number(el.dataset.parallax) || 10;
        gsap.fromTo(el, { yPercent: -amt }, {
          yPercent: amt, ease: 'none',
          scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });

      return () => {
        offs.forEach((off) => off());
        if (raf) cancelAnimationFrame(raf);
      };
    });
  }, { scope });
}